import { useMemo, useState } from 'react';
import { formatJMD } from '../../lib/finances';
import { availableMonths, formatMonth, monthKey, sellThrough } from '../../lib/stockist';

// "Record monthly sales" — the main thing you do to an active consignment.
//
// You pick the month and type how many of each product sold. Everything else
// is computed: what's still out there, what this month is worth, what the
// shop now owes. The point is that you never work out "20 sent, 4 in July,
// 7 in August, so 9 left" yourself — that arithmetic across months is exactly
// what a spreadsheet gets wrong.
export default function RecordSalesDialog({ invoice, statements, editing, onSubmit, onCancel }) {
  const months = useMemo(
    () => availableMonths(invoice, statements),
    [invoice, statements]
  );

  // Editing an existing month keeps that month fixed; a new one defaults to
  // the most recent unreported month, which is nearly always the answer.
  const [month, setMonth] = useState(
    editing ? monthKey(editing.period_month) : months[0] || monthKey(new Date())
  );
  const [quantities, setQuantities] = useState(() => {
    const seed = {};
    for (const line of editing?.invoice_statement_lines || []) {
      seed[line.invoice_item_id] = String(line.quantity_sold);
    }
    return seed;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Sell-through excluding the month being edited, so "remaining" shows what
  // was available to sell *in* that month rather than double-counting the
  // figures currently on screen.
  const others = (statements || []).filter((s) => monthKey(s.period_month) !== month);
  const rows = sellThrough(invoice, others);

  const lines = rows.map((row) => {
    const sold = Number(quantities[row.itemId]) || 0;
    return { ...row, sold, value: sold * row.unitPrice };
  });

  const total = lines.reduce((sum, line) => sum + line.value, 0);
  const units = lines.reduce((sum, line) => sum + line.sold, 0);
  const over = lines.filter((line) => line.sold > line.remaining);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (units === 0) {
      setError('Enter how many sold. If nothing sold, there is no statement to record.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(
        month,
        lines
          .filter((line) => line.sold > 0)
          .map((line) => ({
            invoice_item_id: line.itemId,
            quantity_sold: line.sold,
            // Snapshotted here, so repricing the product later can't rewrite
            // a statement that has already been agreed or paid.
            unit_price: line.unitPrice,
          }))
      );
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Record monthly sales">
      <form className="dialog" onSubmit={handleSubmit}>
        <h3>Record monthly sales</h3>
        <p className="dialog-sub">
          {invoice.invoice_number} · {invoice.bill_to_name}
        </p>

        <label className="field">
          <span>Sales period</span>
          {editing ? (
            <strong className="dialog-fixed">{formatMonth(month)}</strong>
          ) : (
            <select value={month} onChange={(e) => setMonth(e.target.value)}>
              {months.map((m) => (
                <option key={m} value={m}>
                  {formatMonth(m)}
                </option>
              ))}
            </select>
          )}
        </label>

        <table className="sales-entry">
          <thead>
            <tr>
              <th>Product</th>
              <th>Still out</th>
              <th>Sold this month</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.itemId}>
                <td>{line.description}</td>
                <td className="numeric">{line.remaining}</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={quantities[line.itemId] || ''}
                    onChange={(e) =>
                      setQuantities((q) => ({ ...q, [line.itemId]: e.target.value }))
                    }
                    aria-label={`Sold this month: ${line.description}`}
                  />
                </td>
                <td className="numeric">{formatJMD(line.value)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>Payment due for {formatMonth(month)}</td>
              <td className="numeric">{units} units</td>
              <td className="numeric">
                <strong>{formatJMD(total)}</strong>
              </td>
            </tr>
          </tfoot>
        </table>

        {over.length > 0 && (
          <p className="dialog-warning">
            More sold than was sent for {over.map((l) => l.description).join(', ')}. Check the
            figures — if the shop really sold more, the extra was never recorded as sent.
          </p>
        )}

        {error && <p className="po-error">{error}</p>}

        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="primary-action" disabled={saving}>
            {saving ? 'Saving…' : `Record ${formatMonth(month)} sales`}
          </button>
        </div>
      </form>
    </div>
  );
}
